import dayjs, { type Dayjs } from "dayjs";

/** C360's interaction channels, as its API documents them. */
const CHANNEL_LABELS = {
    exwo: "Expert Workspace",
    agent: "Agent",
    chat: "Chat",
    ivr: "IVR",
    web: "Web",
    gaia: "Gaia",
} as const;

/** C360's interaction statuses (`in_progress`, `completed`, `transferred`) and its line statuses (`open`). */
const STATUS_LABELS = {
    open: "Open",
    in_progress: "In progress",
    completed: "Completed",
    transferred: "Transferred",
} as const;

/** A C360 channel as the expert reads it. A channel C360 adds later shows as sent, with its underscores as spaces. */
export function channelLabel(channel: string): string {
    return Object.hasOwn(CHANNEL_LABELS, channel)
        ? CHANNEL_LABELS[channel as keyof typeof CHANNEL_LABELS]
        : channel.replace(/_/g, " ");
}

/** A C360 interaction status as the expert reads it. A status C360 adds later shows as sent, with its underscores as spaces. */
export function statusLabel(status: string): string {
    return Object.hasOwn(STATUS_LABELS, status)
        ? STATUS_LABELS[status as keyof typeof STATUS_LABELS]
        : status.replace(/_/g, " ");
}

/**
 * When an interaction started, short enough to scan ("Today, 10:56 AM", "Yesterday, 3:12 PM", "Oct 20", "Oct 20, 2025")
 * and in full for a tooltip. Undefined when C360 sent no start time or one that isn't a date.
 */
export function startLabels(
    startTime: string | null,
    now: Dayjs = dayjs(),
): { short: string; full: string } | undefined {
    if (!startTime) return undefined;
    const start = dayjs(startTime);
    if (!start.isValid()) return undefined;
    const full = start.format("MMM D, YYYY, h:mm A");
    if (start.isSame(now, "day")) return { short: `Today, ${start.format("h:mm A")}`, full };
    if (start.isSame(now.subtract(1, "day"), "day")) return { short: `Yesterday, ${start.format("h:mm A")}`, full };
    return { short: start.format(start.isSame(now, "year") ? "MMM D" : "MMM D, YYYY"), full };
}
